import type { BuddyBridge, ClaudeAccount, ComputerConnection, SetupState, UpdateStatus, WorkerInfo, WorkerStatus } from "@buddy/desktop/bridge";
import { useCallback, useEffect, useState } from "react";
import { installPreviewBridge } from "./preview-bridge";

// The desktop app injects `window.buddy` through its preload. Everything here is
// inert in a plain browser: `useDesktop()` is undefined and the hooks stay empty.

// Preview builds (VITE_DESKTOP_PREVIEW=1) fake the bridge so the desktop chrome can be
// screenshotted without Electron. Production builds drop this branch entirely.
if (import.meta.env.VITE_DESKTOP_PREVIEW === "1") installPreviewBridge();

export type { BuddyBridge, ClaudeAccount, ComputerConnection, SetupState, UpdateStatus, WorkerInfo, WorkerStatus };

export const useDesktop = (): BuddyBridge | undefined => window.buddy;

/** Mac draws traffic lights in a 38px strip; Windows and Linux keep native caption buttons in 32px. */
export const titleBarStyle = (bridge: BuddyBridge) => (bridge.platform === "darwin" ? "mac" : "windows");

/** Current value from `get`, then every push from `subscribe`. */
const useBridgeValue = <A>(
  get: (() => Promise<A>) | undefined,
  subscribe: ((listener: (value: A) => void) => () => void) | undefined,
) => {
  const [value, setValue] = useState<A>();
  useEffect(() => {
    if (get === undefined || subscribe === undefined) return;
    let live = true;
    const off = subscribe((next) => setValue(next));
    get().then(
      (initial) => {
        if (live) setValue((seen) => seen ?? initial);
      },
      (cause: unknown) => console.warn("[desktop] bridge read failed", cause),
    );
    return () => {
      live = false;
      off();
    };
  }, [get, subscribe]);
  return value;
};

export const useWorkerStatus = () => {
  const bridge = useDesktop();
  return useBridgeValue(bridge?.worker.getStatus, bridge?.worker.subscribe);
};

export const useUpdateStatus = () => {
  const bridge = useDesktop();
  return useBridgeValue(bridge?.updates.getStatus, bridge?.updates.subscribe);
};

/** Re-read whenever `key` changes, such as each time the worker popover opens or the status moves. */
export const useWorkerInfo = (key: unknown) => {
  const bridge = useDesktop();
  const [info, setInfo] = useState<WorkerInfo>();
  useEffect(() => {
    if (bridge === undefined) return;
    let live = true;
    bridge.worker.getInfo().then(
      (next) => live && setInfo(next),
      (cause: unknown) => console.warn("[desktop] worker info failed", cause),
    );
    return () => {
      live = false;
    };
  }, [bridge, key]);
  return info;
};

/** Setup state plus a runner that applies a bridge call's returned state. */
export const useSetupState = () => {
  const bridge = useDesktop();
  const [state, setState] = useState<SetupState>();
  useEffect(() => {
    if (bridge === undefined) return;
    let live = true;
    bridge.setup.get().then(
      (next) => live && setState(next),
      (cause: unknown) => console.warn("[desktop] setup read failed", cause),
    );
    return () => {
      live = false;
    };
  }, [bridge]);
  const apply = useCallback((change: Promise<SetupState>) => {
    change.then(setState, (cause: unknown) => console.warn("[desktop] setup change failed", cause));
  }, []);
  return [state, apply] as const;
};

/**
 * The Claude login employees use on this computer, re-read whenever `key` changes
 * (each time the popover opens), plus `choose`, which asks for another Claude Code folder.
 */
export const useClaudeAccount = (key: unknown) => {
  const bridge = useDesktop();
  const [account, setAccount] = useState<ClaudeAccount>();
  useEffect(() => {
    if (bridge === undefined) return;
    let live = true;
    bridge.claude.get().then(
      (next) => live && setAccount(next),
      (cause: unknown) => console.warn("[desktop] Claude account read failed", cause),
    );
    return () => {
      live = false;
    };
  }, [bridge, key]);
  const choose = useCallback(() => {
    bridge?.claude.choose().then(setAccount, (cause: unknown) => console.warn("[desktop] Claude folder change failed", cause));
  }, [bridge]);
  return [account, choose] as const;
};
